import { z } from 'zod';
import { AutorDaMensagemSchema } from './autoria';
import { LIMITES } from './limites';

import {
  DecisaoNegarSchema,
  DecisaoPermitirSchema,
  DecisaoPlanoSchema,
  DecisaoResponderSchema,
  ModoPermissaoSchema,
} from './painel';
import { MotorSchema, motorNaoTratado } from './motor';
import { EsforcoOpencodeSchema, type EsforcoOpencode } from './opencode';
import { AtualizacaoDePermissaoSchema, TipoPedidoSchema } from './permissoes-sdk';

/** Níveis de esforço do SDK; espelha o `EffortLevel` do pacote. */
const Esforco = z.enum(['low', 'medium', 'high', 'xhigh', 'max']);

/**
 * O esforço que VIAJA no `sdk.iniciar`: o enum do Claude e do Codex, ou a forma
 * do OpenCode (`EsforcoOpencodeSchema`: `padrao` ou o nome de um `variant`).
 *
 * Largo aqui DE PROPÓSITO, e estreitado por motor no `transform` do
 * `SdkIniciarSchema` — o campo sozinho não sabe de qual motor é o pedido. Quem
 * sai do parse nunca vê este tipo largo: sai `SdkIniciarDoFio`, em que o
 * `effort` do Claude e do Codex é o enum de sempre.
 *
 * Por que não somar `padrao` ao `Esforco`: aquele é o `EffortLevel` do SDK do
 * Claude e aparece em `ModeloDisponivel.esforcos`, `SdkCapacidades.atual` e
 * `TrabalhoDeTexto` — alargá-lo punha um valor do OpenCode ao alcance do CLI do
 * Claude em quatro lugares, e nenhum deles tem motor para recusar.
 */
const EsforcoDoIniciar = z.union([Esforco, EsforcoOpencodeSchema]);

/**
 * Os modos que cada motor NÃO pode receber, com o porquê de cada um.
 *
 * `switch` exaustivo terminando em `motorNaoTratado`: um quarto motor não
 * compila até alguém decidir, aqui, o que ele recusa. O `if (motor !== 'CODEX')
 * return` que existia antes deixava todo motor novo passar como Claude.
 */
function recusarModoDoMotor(
  v: { motor: z.infer<typeof MotorSchema>; modo: z.infer<typeof ModoPermissaoSchema>; mestre: boolean },
  ctx: z.RefinementCtx,
): void {
  const recusar = (mensagem: string): void => {
    ctx.addIssue({ code: 'custom', path: ['modo'], message: mensagem });
  };
  switch (v.motor) {
    case 'CLAUDE':
      // O Claude tem os seis modos: é a lista de onde os outros tiram.
      return;
    case 'CODEX':
      if (v.modo === 'plan') {
        // O Codex não tem `ExitPlanMode`: o `plan` dele é lista de tarefas do
        // próprio agente, que não espera decisão de ninguém. Um modo que nunca
        // produz o cartão que o destrava é uma sessão que nasce travada, e sem
        // como destravar pelo painel.
        recusar('modo_indisponivel_no_motor: o Codex nao tem plano, entao `plan` nunca sairia');
      }
      if (v.modo === 'dontAsk') {
        // Mesma família do anterior, e a razão está escrita em
        // `.claude/rules/seguranca.md`: `dontAsk` emperra a sessão sem dar como
        // destravar. No Claude ele existe por herança; no motor novo não nasce.
        recusar('modo_indisponivel_no_motor: `dontAsk` emperra a sessao sem dar como destravar');
      }
      return;
    case 'OPENCODE':
      // D15: `default`, `acceptEdits`, `plan` (o agente `plan` nativo, só
      // leitura) e `bypassPermissions`. `auto` é um CLASSIFICADOR do Claude
      // Code — o OpenCode não tem nada que aprove, negue e escale sozinho, e
      // "auto" ali seria um nome sem mecanismo por trás.
      if (v.modo === 'auto') {
        recusar('modo_indisponivel_no_motor: o OpenCode nao tem classificador, entao `auto` nao existe');
      }
      if (v.modo === 'dontAsk') {
        recusar('modo_indisponivel_no_motor: `dontAsk` emperra a sessao sem dar como destravar');
      }
      // A mestre do OpenCode nasce em `bypassPermissions` e não sai dele (D3,
      // Interface congelada item 2): a trava dela é a deny-list da camada
      // gerenciada, não o modo. Um `iniciar` de mestre noutro modo é a API
      // errando, e o daemon não deve adivinhar qual dos dois vale.
      if (v.mestre && v.modo !== 'bypassPermissions') {
        recusar('modo_fixo_da_mestre_opencode: a mestre do OpenCode so nasce em `bypassPermissions`');
      }
      return;
    default:
      motorNaoTratado(v.motor);
  }
}

/**
 * Ordem de iniciar uma sessao na maquina: os campos, antes do estreitamento
 * por motor que o `SdkIniciarSchema` faz.
 *
 * `modelo` e `effort` viajam sempre preenchidos: o settings.json do usuario
 * pode ter Fable como padrao, e Fable cobra credito extra sem perguntar. Nunca
 * deixar o subprocesso escolher sozinho.
 */
const SdkIniciarCamposSchema = z.object({
  sessaoId: z.string().min(1),
  cwd: z.string().min(1),
  modo: ModoPermissaoSchema,
  modelo: z.string().min(1),
  /** Largo aqui; o `transform` do `SdkIniciarSchema` estreita por motor. */
  effort: EsforcoDoIniciar,
  primeiraMensagem: z.string().max(LIMITES.textoMensagem).optional(),
  /**
   * Quem escreveu a `primeiraMensagem` (`autoria.ts`).
   *
   * Ela não passa pelo `sdk.enviar` — o host a empurra na fila ao nascer a
   * sessão —, então sem este campo a PRIMEIRA mensagem de toda conversa seria
   * a única sem nome. Campo novo e opcional: não sobe `VERSAO_PROTOCOLO`.
   */
  autor: AutorDaMensagemSchema.optional(),
  /** Retomar uma sessao que dormiu, em vez de comecar do zero. */
  retomar: z.string().optional(),
  /**
   * Bifurcar em vez de entrar na conversa retomada: id novo, arquivo novo,
   * historico inteiro.
   *
   * E o que separa "continuar aqui" de "assumir". Sem isto, retomar a conversa
   * aberta no VS Code cria duas linhas com o MESMO `sessionIdClaude` — que
   * colide com a chave unica `(maquina, id_no_motor)` — e deixa um galho
   * orfao que ninguem ve. Medido; esta em docs/estudos/paridade-com-o-vs-code.md.
   */
  bifurcar: z.boolean().default(false),
  /**
   * Sessão mestre (F13): o host injeta o servidor MCP `remoto`, o systemPrompt
   * do mestre, e NÃO conta a vaga do semáforo. Daemon sem `MESTRE_HABILITADO`
   * recusa. `.default(false)` mantém compatível: daemon antigo descarta a
   * chave, e é a sonda de capacidades quem impede a API de mandar para ele.
   */
  mestre: z.boolean().default(false),
  /**
   * O botão "Compactação automática do Remoto" (R4). Viaja no `sdk.iniciar`
   * porque acordar uma conversa manda o pedido de novo: sem o campo, a
   * escolha se perderia a cada sono. `.default(true)`: ausente é ligado.
   */
  compactacaoAutomatica: z.boolean().default(true),
  /**
   * Qual CLI atende esta sessão. `.default('CLAUDE')` porque toda carga que não
   * fala de motor É Claude: sem isso, o eixo novo viraria uma migração de
   * chamadores em vez de um acréscimo.
   */
  motor: MotorSchema,
});

type CamposDoIniciar = Omit<z.output<typeof SdkIniciarCamposSchema>, 'motor' | 'effort'>;

/**
 * O pedido que os hosts do Claude e do Codex recebem: `effort` é o enum de
 * sempre, que é o `EffortLevel` do SDK do Claude.
 */
export type SdkIniciarClaudeOuCodex = CamposDoIniciar & {
  motor: 'CLAUDE' | 'CODEX';
  effort: z.infer<typeof Esforco>;
};

/** O pedido que o host do OpenCode recebe: `effort` é `padrao` ou um `variant`. */
export type SdkIniciarOpencode = CamposDoIniciar & {
  motor: 'OPENCODE';
  effort: EsforcoOpencode;
};

/**
 * Tudo o que o fio carrega: é o que sai do parse, e é o que a fachada recebe.
 * Quem entrega a um host estreita por `motor` antes — e o compilador exige.
 */
export type SdkIniciarDoFio = SdkIniciarClaudeOuCodex | SdkIniciarOpencode;

/**
 * **O nome antigo ficou com a metade Claude/Codex, e é decisão.** `SdkIniciar`
 * é o tipo que `sdk/host.ts` e `codex/host.ts` recebem, e os dois passam o
 * `effort` direto ao motor (`EffortLevel` do SDK, `ReasoningEffort` do
 * app-server). Se o nome passasse a significar o fio inteiro, um `padrao` do
 * OpenCode caberia no tipo do host do Claude — e nenhum dos dois arquivos pode
 * ser tocado por este plano. Assim o tipo continua dizendo a verdade para quem
 * já o usava, e o motor novo tem o dele.
 */
export type SdkIniciar = SdkIniciarClaudeOuCodex;

/**
 * Estreita o esforço pelo motor — o `none` do plano virou `padrao` pela
 * medição da Fase 0 (Interface congelada, item 3 revisto).
 *
 * - **CLAUDE e CODEX**: o enum de sempre. `padrao` é recusado — o CLI do
 *   Claude não o conhece, e o Codex sem esforço cairia no padrão do
 *   app-server, que é a escolha silenciosa que a regra "modelo e esforço
 *   explícitos" existe para impedir.
 * - **OPENCODE**: `EsforcoOpencodeSchema`. Se aquele `variant` existe para
 *   aquele modelo é pergunta do catálogo, e quem responde é a API.
 *
 * `switch` exaustivo: um quarto motor não compila até alguém decidir aqui.
 */
function estreitarPorMotor(
  v: z.output<typeof SdkIniciarCamposSchema>,
  ctx: z.core.$RefinementCtx<z.output<typeof SdkIniciarCamposSchema>>,
): SdkIniciarDoFio {
  const recusar = (mensagem: string): never => {
    ctx.issues.push({ code: 'custom', path: ['effort'], message: mensagem, input: v.effort });
    return z.NEVER;
  };
  switch (v.motor) {
    case 'CLAUDE':
    case 'CODEX': {
      const lido = Esforco.safeParse(v.effort);
      if (!lido.success) {
        return recusar(
          'esforco_indisponivel_no_motor: ' + v.motor + ' aceita low, medium, high, xhigh e max',
        );
      }
      return { ...v, motor: v.motor, effort: lido.data };
    }
    case 'OPENCODE': {
      const lido = EsforcoOpencodeSchema.safeParse(v.effort);
      if (!lido.success) {
        return recusar('esforco_indisponivel_no_motor: o OpenCode aceita `padrao` ou o nome de um variant');
      }
      return { ...v, motor: v.motor, effort: lido.data };
    }
    default:
      return motorNaoTratado(v.motor);
  }
}

/**
 * Ordem de iniciar uma sessao na maquina.
 *
 * O modo e o esforço que cada motor NÃO pode receber são recusados AQUI, e não
 * numa checagem que alguém esquece de chamar — é a mesma escolha do canal do
 * mestre, onde `dontAsk` e Fable não existem no schema. O `transform` final é
 * o que faz a saída do parse ser `SdkIniciarDoFio`, com o `effort` já do tipo
 * do motor: a fachada do daemon não precisa de `as` para entregar ao host.
 */
export const SdkIniciarSchema = SdkIniciarCamposSchema.superRefine(recusarModoDoMotor).transform(
  estreitarPorMotor,
);

/**
 * Um anexo a caminho da máquina.
 *
 * Vai a URL assinada de leitura, não os bytes: o quadro do socket do agente
 * tem teto e um anexo de 5 MB não caberia. Quem baixa é o daemon, que grava o
 * arquivo na pasta de anexos dele e passa o caminho para a sessão.
 */
export const AnexoParaMaquinaSchema = z.object({
  chave: z.string().min(1),
  urlGet: z.string().min(1),
  mime: z.string().min(1),
  nome: z.string().min(1),
});
export type AnexoParaMaquina = z.infer<typeof AnexoParaMaquinaSchema>;

export const SdkEnviarSchema = z.object({
  sessaoId: z.string().min(1),
  texto: z.string().min(1).max(LIMITES.textoMensagem),
  anexos: z.array(AnexoParaMaquinaSchema).max(LIMITES.anexosPorMensagem).default([]),
  /**
   * Se esta mensagem abre um turno NOVO de despachos da mestre.
   *
   * O teto de despachos é por turno, e turno é gente falando. Recado de outra
   * mestre e cutucão sobre outra mestre chegam por aqui também — e zerar o
   * teto neles dava cinco despachos novos a cada volta do laço entre as duas,
   * sem humano no meio (revisão do PR 58, 2.2.8). Quem sabe de onde a mensagem
   * veio é o servidor, e é ele que marca `false`. Ausente é `true`: o painel é
   * gente. Campo novo com `.default()`, então não sobe versão.
   */
  contaComoTurnoNovo: z.boolean().default(true),
  /**
   * Quem está mandando (`autoria.ts`). Quem sabe é o SERVIDOR: o daemon não
   * conhece usuários, e um autor montado na máquina seria palpite.
   *
   * Ausente é "não se sabe" e o evento nasce sem nome — nunca um padrão
   * silencioso. Campo novo e opcional: não sobe `VERSAO_PROTOCOLO`.
   */
  autor: AutorDaMensagemSchema.optional(),
  /**
   * O modelo que atende ESTE turno, quando não é o da sessão (F17, D23 revista).
   *
   * Só o OpenCode o lê: um turno com imagem vai ao `modeloDeVisao` da config da
   * frota, e a config mora na API — é ela que decide, por
   * `modeloDoTurnoComImagem`, e manda o resultado aqui. O daemon não guarda cópia
   * nenhuma da config: uma segunda cópia envelheceria calada no dia em que o
   * dono trocasse o modelo de visão na tela.
   *
   * Opcional e SEM `.default()`: ausente é "o modelo da sessão", que é o que um
   * turno sem imagem (e toda API anterior a este campo) quer dizer. Campo novo e
   * opcional: não sobe `VERSAO_PROTOCOLO`.
   */
  modeloDoTurno: z.string().min(1).max(200).optional(),
});
export type SdkEnviar = z.infer<typeof SdkEnviarSchema>;

/**
 * Mensagem para uma sessão que o painel só observa.
 *
 * Vai pelo canal local do Claude Code (o `messagingSocketPath` que cada sessão
 * viva publica), não pelo SDK: esta sessão pertence ao VS Code, e o daemon só
 * entrega o recado.
 */
export const MensagemLocalSchema = z.object({
  /*
    `sessionIdClaude`, e não `idNoMotor`: é decisão, e tem teste que a prende
    (`sdk.spec.ts`, "o renome no fio"). Este canal fala com o
    `messagingSocketPath` do Claude Code — mundo 100% Claude —, e ali o nome
    DIZ de qual motor é o dado.

    **E a API mandava a chave errada.** Achado em 2026-09-21: o emissor
    (`sessoes.service#enviar`) montava `idNoMotor`, este parse recusava, e o
    daemon respondia "pedido fora do formato" — que a API traduz em "a mensagem
    nao chegou a esta sessao". TODA mensagem do painel para sessão observada
    morria aí. O e2e conferia a carga que a API EMITE, nunca que o daemon a
    aceita, e por isso passava verde sobre o caminho quebrado: mais um
    instrumento medindo o próprio término. Hoje o e2e valida a carga com ESTE
    schema.
  */
  sessionIdClaude: z.string().min(1),
  texto: z.string().min(1).max(LIMITES.textoMensagem),
  /**
   * Anexos chegam por caminho de arquivo, não por bloco de imagem: o canal
   * local do Claude Code só transporta texto no campo `content`.
   */
  anexos: z.array(AnexoParaMaquinaSchema).max(LIMITES.anexosPorMensagem).default([]),
  /**
   * Quem está mandando (`autoria.ts`).
   *
   * Existe aqui, e não só no `sdk.enviar`, porque o canal local é exatamente
   * o caminho que faria o painel MENTIR: uma conversa do editor é rotulada
   * "Editor" por não ter autor nenhum, e a mensagem que o painel manda para
   * ela cairia na mesma vala. Medido em 2026-09-21: o quadro de peer aceita um
   * `uuid` nosso e o CLI o escreve no transcript, então esta ponta tem vínculo
   * exato como as outras duas.
   */
  autor: AutorDaMensagemSchema.optional(),
});
export type MensagemLocal = z.infer<typeof MensagemLocalSchema>;

export const SdkComandoSchema = z.object({ sessaoId: z.string().min(1) });
export type SdkComando = z.infer<typeof SdkComandoSchema>;

/**
 * Encerrar. `recusarEmTurno` existe para a MESTRE, e nasce desligado para o
 * painel: quem clica em Encerrar ja leu o aviso de que nao tem volta e esta
 * decidindo com a conversa na frente. A mestre nao tem essa frente, entao ela
 * pede a recusa — e so passa por cima com `forcar` explicito.
 */
export const SdkFecharSchema = z.object({
  sessaoId: z.string().min(1),
  recusarEmTurno: z.boolean().default(false),
});
export type SdkFechar = z.infer<typeof SdkFecharSchema>;

export const SdkModoSchema = z.object({
  sessaoId: z.string().min(1),
  modo: ModoPermissaoSchema,
});
export type SdkModo = z.infer<typeof SdkModoSchema>;

/** Liga ou desliga a compactação do Remoto numa sessão viva (R4). */
export const SdkCompactacaoSchema = z.object({
  sessaoId: z.string().min(1),
  automatica: z.boolean(),
});
export type SdkCompactacao = z.infer<typeof SdkCompactacaoSchema>;

export const SdkModeloSchema = z.object({
  sessaoId: z.string().min(1),
  modelo: z.string().min(1),
  /**
   * O vocabulário de CADA motor (F17, L3). O comando não diz o motor, por isso
   * o fio aceita a união: o enum do Claude/Codex (que vai por
   * `applyFlagSettings` no SDK) e o do OpenCode (`padrao` ou o nome de um
   * variant do modelo, que vai em cada prompt). Quem confere de verdade é a API,
   * contra o catálogo do motor da sessão, e de novo cada host no daemon: o
   * adaptador do Claude e o do Codex recusam o que não é do enum deles.
   */
  esforco: z.union([Esforco, EsforcoOpencodeSchema]).optional(),
  confirmacaoFable: z.boolean().default(false),
});
export type SdkModelo = z.infer<typeof SdkModeloSchema>;

/**
 * Resposta do daemon a qualquer comando de sessao.
 *
 * **`strictObject`, e isso e o ponto.** `z.object` DESCARTA chave desconhecida
 * em silencio: um daemon que ainda mandasse `sessionIdClaude` passaria pelo
 * parse com `idNoMotor` AUSENTE, e a linha da sessao ficaria em `pendente:`
 * para sempre — sem erro em lugar nenhum, sem log, sem sintoma alem de uma
 * sessao que nunca sai do lugar.
 *
 * A porta de versao do `agente.ola` (`VERSAO_AGENTE_MINIMA`) ja garante que
 * daemon antigo nem conecta, entao esta recusa nao derruba ninguem legitimo.
 * Ela existe para o dia em que a porta falhar: duas barreiras independentes,
 * porque degradar calado e o pior desfecho possivel aqui.
 */
export const SdkRespostaSchema = z.discriminatedUnion('ok', [
  z.strictObject({ ok: z.literal(true), idNoMotor: z.string().optional() }),
  z.strictObject({
    ok: z.literal(false),
    /**
     * `EM_TURNO` e a recusa do adormecer enquanto o modelo trabalha: dormir e
     * fechar a fila, e fechar a fila no meio do turno DESCARTA o que estava em
     * voo. Erro proprio, e nao `FALHA`: `FALHA` e traduzida como problema de
     * maquina, e mandaria investigar rede numa recusa deliberada.
     */
    erro: z.enum([
      'SEM_CAPACIDADE',
      'SEM_MEMORIA',
      'CWD_INVALIDO',
      'NAO_ENCONTRADA',
      'EM_TURNO',
      'FALHA',
    ]),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type SdkResposta = z.infer<typeof SdkRespostaSchema>;

/** Pedido de permissao que o daemon manda quando a sessao trava esperando. */
export const PermissaoPedidoSchema = z.object({
  sessaoId: z.string().min(1),
  requestId: z.string().min(1),
  origem: z.enum(['SDK', 'GANCHO']),
  /** Decide o cartão: permissão comum, pergunta, plano… */
  tipo: TipoPedidoSchema.default('FERRAMENTA'),
  ferramenta: z.string().min(1),
  entrada: z.record(z.string(), z.unknown()),
  /** As sugestões prontas do SDK ("sempre permitir…"), agora tipadas. */
  sugestoes: z.array(AtualizacaoDePermissaoSchema).max(10).optional(),
  /** O 3º argumento do canUseTool, que antes era jogado fora. */
  toolUseId: z.string().max(200).optional(),
  agenteId: z.string().max(200).optional(),
  titulo: z.string().max(500).optional(),
  rotuloCurto: z.string().max(200).optional(),
  descricao: z.string().max(2_000).optional(),
  motivo: z.string().max(500).optional(),
  /** Só o pedido de gancho expira: do outro lado há um prompt local esperando. */
  expiraEm: z.iso.datetime().optional(),
});
export type PermissaoPedido = z.infer<typeof PermissaoPedidoSchema>;

/**
 * A decisão, na perna API → daemon: a MESMA união do painel com `requestId`.
 *
 * O `permitir: boolean` de antes morreu — e com ele o bump para a versão 2 do
 * protocolo: daemon antigo é recusado em vez de adivinhar o formato.
 */
export const PermissaoDecididaSchema = z.discriminatedUnion('decisao', [
  DecisaoPermitirSchema.extend({ requestId: z.string().min(1) }),
  DecisaoResponderSchema.extend({ requestId: z.string().min(1) }),
  DecisaoPlanoSchema.extend({ requestId: z.string().min(1) }),
  DecisaoNegarSchema.extend({ requestId: z.string().min(1) }),
]);
export type PermissaoDecidida = z.infer<typeof PermissaoDecididaSchema>;

/**
 * Trabalho de texto sem interface: resumo, ata, pergunta e Ctrl+K.
 *
 * A instrucao viaja junto porque ela e contrato - a ata de uma reuniao e
 * documento, e quem escreve a instrucao e o repositorio, nao o daemon.
 */
export const TrabalhoDeTextoSchema = z.object({
  instrucao: z.string().min(1).max(8_000),
  conteudo: z.string().min(1).max(200_000),
  modelo: z.string().min(1),
  effort: Esforco,
  cwd: z.string().optional(),
  podeLer: z.boolean().default(false),
  /** Ctrl+K: a resposta é um arquivo, e vem cercada por bloco de código. */
  desembrulhar: z.boolean().default(false),
});
export type TrabalhoDeTexto = z.infer<typeof TrabalhoDeTextoSchema>;

export const RespostaDeTextoSchema = z.object({
  ok: z.boolean(),
  texto: z.string().optional(),
  erro: z.string().max(300).optional(),
});
export type RespostaDeTexto = z.infer<typeof RespostaDeTextoSchema>;

/**
 * Estado da sessao do SDK, para o painel acompanhar sem perguntar.
 *
 * `strictObject` pela MESMA razao do `SdkRespostaSchema` acima, e aqui o
 * sintoma e ainda mais mudo: e este evento que preenche o `idNoMotor` da linha
 * da sessao. Chave velha descartada em silencio deixaria a sessao viva na
 * maquina e sem id no painel — para sempre.
 */
export const SdkEstadoSchema = z.strictObject({
  sessaoId: z.string().min(1),
  estado: z.enum(['ATIVA', 'DORMINDO', 'AGUARDANDO_PERMISSAO', 'ENCERRADA']),
  idNoMotor: z.string().optional(),
  erro: z.string().max(300).optional(),
});
export type SdkEstado = z.infer<typeof SdkEstadoSchema>;

/**
 * Um comando de barra que a sessão oferece. O SDK trata skill e comando como
 * a mesma coisa; `ehSkill` existe só para a tela poder pôr um selo.
 */
export const ComandoDeBarraSchema = z.object({
  nome: z.string().min(1).max(200),
  descricao: z.string().max(2_000).default(''),
  dicaDeArgumento: z.string().max(300).default(''),
  aliases: z.array(z.string().max(200)).max(10).default([]),
  /** Preso ao terminal local (exit, statusline…): o painel esconde. */
  soNoTerminal: z.boolean().default(false),
  ehSkill: z.boolean().default(false),
});
export type ComandoDeBarra = z.infer<typeof ComandoDeBarraSchema>;

/** Um modelo que o CLI da máquina sabe usar, com os esforços que aceita. */
export const ModeloDisponivelSchema = z.object({
  id: z.string().min(1).max(200),
  /** Para casar apelido com id canônico ('sonnet' → 'claude-sonnet-5'). */
  resolvido: z.string().max(200).optional(),
  rotulo: z.string().min(1).max(200),
  descricao: z.string().max(2_000).default(''),
  aceitaEsforco: z.boolean().default(false),
  esforcos: z.array(Esforco).max(5).default([]),
  /*
    Os esforços que NÃO cabem no enum — e isto não é hipótese, é M0-3: o Codex
    oferece `ultra` ("maximum reasoning with automatic task delegation"), que o
    `EsforcoSchema` (`low|medium|high|xhigh|max`) não tem, e oferece SEIS níveis
    onde o `.max(5)` acima cabe cinco. Os dois motivos são independentes: mesmo
    que o nome coubesse, a contagem não caberia.

    Por que campo novo em vez de alargar `Esforco` para string: alargar mudaria
    a forma do lado CLAUDE, onde o enum fechado é o que faz o seletor não
    oferecer um esforço que o CLI recusa — pagar isso para o motor que não tem
    o problema é trocar uma garantia por uniformidade.

    Lista de strings livres porque o vocabulário é de QUEM SERVE o modelo: o dia
    em que a OpenAI somar um nível, ele atravessa sem release nossa. A tela
    mostra o id cru, e id feio é melhor que nome inventado.
  */
  esforcosDoMotor: z.array(z.string().min(1).max(40)).max(12).default([]),
  /*
    O `isDefault` da máquina, não um palpite nosso. Sem ele, escolher "o
    primeiro da lista" seria o código inventando qual modelo é o padrão — e a
    ordem de `model/list` é da OpenAI, não promessa nenhuma.

    O schema não garante UM só: garantir unicidade aqui é impossível (o campo é
    de item, não da lista), e quem consome pega o primeiro `padrao`. Duas
    marcas seriam a máquina se contradizendo, e o painel não inventa desempate.
  */
  padrao: z.boolean().default(false),
  /*
    `defaultReasoningEffort`. String livre, e não `Esforco`, porque ele APONTA
    para um item das listas acima — e uma delas carrega `ultra`. Hoje os seis
    modelos medidos trazem `low` ou `medium`, que caberiam no enum: tipar por
    essa coincidência é escrever uma armadilha que só dispara no dia em que a
    OpenAI mudar o padrão, e aí o esforço sumiria em silêncio.
  */
  esforcoPadrao: z.string().min(1).max(40).nullable().default(null),
});
export type ModeloDisponivel = z.infer<typeof ModeloDisponivelSchema>;

/**
 * O que a sessão do SDK oferece agora: comandos, skills, modelos, agentes e o
 * estado corrente. Volátil de propósito — nunca vira coluna; a máquina é a
 * fonte, e o painel degrada para listas estáticas quando ela está desligada.
 *
 * Extensível por regra: campo novo entra opcional com `.default()`, e quem
 * precisar de mais acrescenta chave AQUI, não um evento novo. Mudou a forma,
 * sobe `versao` e o lado antigo descarta em vez de adivinhar.
 */
export const SdkCapacidadesSchema = z.object({
  sessaoId: z.string().min(1),
  /*
    Continua 1, e de propósito: os três campos que o motor Codex trouxe para o
    `ModeloDisponivel` (`esforcosDoMotor`, `padrao`, `esforcoPadrao`) entraram
    com `.default()`, que é exatamente o acréscimo que a regra acima permite sem
    bump. Daemon que não os conhece manda o catálogo de sempre e ele continua
    valendo; painel que não os lê ignora. Subir por acréscimo opcional gastaria
    o número que existe para avisar de mudança de FORMA — e no dia da mudança de
    verdade ninguém saberia distinguir uma da outra.
  */
  versao: z.literal(1),
  em: z.iso.datetime(),
  comandos: z.array(ComandoDeBarraSchema).max(500).default([]),
  modelos: z.array(ModeloDisponivelSchema).max(60).default([]),
  agentes: z
    .array(z.object({ nome: z.string().min(1).max(200), descricao: z.string().max(2_000).default('') }))
    .max(200)
    .default([]),
  /** O que a sessão está usando agora, direto do `init` de cada turno. */
  atual: z
    .object({
      modelo: z.string().max(200).optional(),
      esforco: Esforco.nullable().optional(),
      modo: ModoPermissaoSchema.optional(),
      estiloDeSaida: z.string().max(200).optional(),
    })
    .default({}),
  /** O `capabilities` do init: feature-detect, nunca farejar versão. */
  recursos: z.array(z.string().max(100)).max(50).default([]),
});
export type SdkCapacidades = z.infer<typeof SdkCapacidadesSchema>;

/**
 * Um subagente em curso, do ponto de vista do stream do SDK.
 *
 * O cartão da conversa já monta o grupo pelo `isSidechain` do transcript; o que
 * só existe aqui é o `id` da tarefa — sem ele não há como parar UM subagente,
 * e a única saída era interromper a sessão inteira.
 */
export const TarefaViva = z.object({
  id: z.string().min(1),
  /** Casa com o bloco `tool_use` do Task na conversa. */
  toolUseId: z.string().max(200).optional(),
  descricao: z.string().max(500).default(''),
  subtipo: z.string().max(100).optional(),
  estado: z.enum(['rodando', 'concluida', 'falhou', 'parada', 'pausada']),
  resumo: z.string().max(2_000).optional(),
});
export type TarefaViva = z.infer<typeof TarefaViva>;

export const SdkTarefasSchema = z.object({
  sessaoId: z.string().min(1),
  tarefas: z.array(TarefaViva).max(100).default([]),
});
export type SdkTarefas = z.infer<typeof SdkTarefasSchema>;

/**
 * Um pedaço da resposta, enquanto ela está sendo escrita.
 *
 * **Volátil, como as tarefas e as capacidades**: não tem `seq`, não é gravado
 * e não entra em replay. O texto da conversa continua vindo do transcript, que
 * é append-only e sobrevive a recarregar — este canal é espelho de progresso, e
 * **se os dois divergirem, o transcript vence**.
 *
 * O teto de `texto` é a folga da represa do daemon (4000 chars): ela solta a
 * cada ~150 ms ou quando enche, então um quadro maior que isto é sinal de
 * emissor errado, não de resposta longa.
 */
export const SdkParcialSchema = z.object({
  sessaoId: z.string().min(1),
  texto: z.string().min(1).max(8_000),
});
export type SdkParcial = z.infer<typeof SdkParcialSchema>;

/** Parar UM subagente, sem derrubar a sessão. */
export const SdkPararTarefaSchema = z.object({
  sessaoId: z.string().min(1),
  tarefaId: z.string().min(1),
});
export type SdkPararTarefa = z.infer<typeof SdkPararTarefaSchema>;

/**
 * O turno terminou.
 *
 * O daemon relata o fato bruto; quem decide se isso vira aviso é a API — limiar
 * de duração, silêncio por sessão e preferência da pessoa são política, e
 * política no daemon exigiria republicar o agente em toda máquina para mudar
 * um número.
 *
 * Vem dos dois mundos: da mensagem `result` do SDK, nas sessões criadas pelo
 * painel, e do gancho `Stop`, nas observadas — que já era instalado e jogado
 * fora. Para `GANCHO` o `sessaoId` é o id do Claude, como no
 * `PermissaoPedidoSchema`: quem traduz para o id da linha é a API.
 */
export const SessaoTurnoFimSchema = z.object({
  sessaoId: z.string().min(1),
  origem: z.enum(['SDK', 'GANCHO']),
  duracaoMs: z.number().int().nonnegative().optional(),
  custoUsd: z.number().nonnegative().optional(),
  ehErro: z.boolean().default(false),
  /**
   * POR QUE terminou mal — a mensagem do motor, como ela veio.
   *
   * `ehErro` diz que terminou mal e não diz mais nada; foi com ele sozinho que
   * a cota esgotada do Codex chegou ao servidor como um turno "COM ERRO" sem
   * causa nenhuma (2026-09-21). Campo novo, opcional e sem `.default()`: o
   * daemon antigo não o manda e continua válido, e ausente significa "a
   * máquina não disse", nunca "não houve motivo".
   */
  motivo: z.string().max(300).optional(),
  /*
    Os três abaixo são do ADR-0021 (economia de tokens), e seguem o precedente
    do `motivo`: opcionais e SEM `.default()`. Ausente significa "a máquina não
    disse" — daemon anterior à 0.6.0 —, nunca "vazio", "turno comum" ou "zero".
  */
  /**
   * O texto final do turno, cortado pelo daemon. Claude: `SDKResultSuccess.result`;
   * Codex: o último `agentMessage` que é resposta. A vigília leva este texto no
   * recado ao mestre como DADO, para ele não precisar abrir a conversa (leitura
   * que custa contexto inteiro) só para saber como o turno acabou.
   */
  desfecho: z.string().max(1_500).optional(),
  /**
   * Por que este turno existiu, quando não foi uma mensagem de alguém. Hoje só
   * a autocompactação de sessão ociosa: a API não avisa ninguém de um turno
   * que ninguém pediu. Enum fechado — valor desconhecido é recusado, e não
   * tratado como turno comum.
   */
  natureza: z.enum(['compactacao']).optional(),
  /**
   * O contexto da ÚLTIMA chamada do turno, em tokens. Claude: `input +
   * cache_read + cache_creation` do último `usage`; Codex: `input_tokens` do
   * último `tokenUsage`. É o que decide a compactação e o aviso acima de 150k.
   */
  contextoTokens: z.number().int().nonnegative().optional(),
});
export type SessaoTurnoFim = z.infer<typeof SessaoTurnoFimSchema>;

/** Resposta do daemon ao `sdk.capacidades.ler`. */
export const SdkCapacidadesRespostaSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), capacidades: SdkCapacidadesSchema }),
  z.object({
    ok: z.literal(false),
    erro: z.enum(['NAO_ENCONTRADA', 'FALHA']),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type SdkCapacidadesResposta = z.infer<typeof SdkCapacidadesRespostaSchema>;
